/**
 * equipmentRegistrationFlowStore — Zustand store for live EquipmentRegistrationFlow snapshots.
 * (#426, card-driven cadastro)
 *
 * Mirrors the onboardingFlowStore pattern: single source of truth for
 * `equipment_registration_flow_state_update` WebSocket events emitted by the
 * engine whenever the EquipmentRegistrationFlow state machine advances.
 *
 * Keyed by `flowId`. `EquipmentRegistrationCard` subscribes directly — no
 * prop drilling beyond the flowId.
 *
 * RAIZ 3 (2026-07-25) — ESTA STORE NÃO CONHECE A ORDEM DAS ETAPAS. Ela existia
 * aqui como `STATE_ORDER` e era usada para decidir se um snapshot recebido era
 * "mais avançado" que o guardado. Essa lista ficou defasada da tabela do motor
 * (`EQUIPMENT_REGISTRATION_TRANSITIONS`, engine
 * `src/services/equipment-registration/flow-types.ts`), onde a etapa do
 * equipamento vem DEPOIS da localização: o snapshot legítimo da etapa 3 era
 * descartado, o card ficava preso na localização, e TAG, criticidade e o
 * `confirm` nunca eram alcançados. A ordem passa a ter UMA fonte só, o motor, e
 * a guarda anti-snapshot-velho compara o `revision` que o motor emite junto do
 * snapshot (versão monotônica da linha). Reordenar etapas no motor não exige
 * mais nenhuma mudança aqui.
 */
import { create, type StoreApi } from 'zustand'
import type {
  EquipmentRegistrationActionPayload,
  EquipmentRegistrationFlowContext,
  EquipmentRegistrationFlowSnapshot,
  EquipmentRegistrationFlowStateUpdateEvent,
  EquipmentRegistrationFlowState,
} from '../types/rich-content.js'
import type {
  CodigoDeConflitoDeCadastro,
  CodigoDeRecusaDeCadastro,
} from '@rochadenis/axiomgo-contracts/flows/equipment-registration'
import type {
  EnvelopeEmUso,
  LocalizacaoConhecida,
} from '@/lib/equipment-registration/aplicar-clique-local'

/**
 * A versão que o motor emite com o snapshot, quando ela existe. Envelope legado
 * (persistido antes deste contrato) não tem: `undefined` significa "idade
 * desconhecida", nunca "mais novo".
 */
function revisionOf(snapshot: { revision?: number }): number | undefined {
  return typeof snapshot.revision === 'number' ? snapshot.revision : undefined
}

/**
 * O incoming pode substituir o guardado?
 *
 *  - ambos versionados: só se for igual ou mais novo. Igual é aceito porque o
 *    MESMO turno chega pelos dois canais (rich content e frame do WebSocket) e o
 *    segundo a chegar não pode ser tratado como velho.
 *  - guardado versionado e incoming sem versão: NÃO. Um envelope de idade
 *    desconhecida nunca sobrescreve um estado versionado (é o replay de
 *    mensagem antiga que a guarda antiga tentava barrar por ranking de etapa).
 *  - nada guardado, ou guardado sem versão: sim, último a chegar vale.
 */
function canReplace(
  existing: EquipmentRegistrationFlowSnapshot | undefined,
  incoming: { revision?: number },
): boolean {
  if (!existing) return true
  const existingRevision = revisionOf(existing)
  if (existingRevision === undefined) return true
  const incomingRevision = revisionOf(incoming)
  if (incomingRevision === undefined) return false
  return incomingRevision >= existingRevision
}

/**
 * O rascunho do cadastro rodando no navegador (spec 2026-10-24 §2.2). Só
 * memória: recarregar a página volta ao último snapshot do servidor.
 */
export interface RascunhoDeCadastro {
  flowId: string
  conversationId: string
  /** `tenantId|userId` de quem abriu o rascunho — cerca de dono (P4). */
  dono: string
  /** A revision do SERVIDOR sobre a qual o rascunho nasceu. */
  base: { revision: number }
  /**
   * A revision do snapshot da MENSAGEM cujo cartão hospeda o rascunho. Igual a
   * `base` no primeiro rascunho; depois de um sync sem cartão novo, o mesmo
   * cartão hospeda rascunhos de base mais nova — o registro do histórico é
   * sempre o do cartão (`chaveDoHospedeiro(flowId, hospedeiro)`), o mais recente.
   */
  hospedeiro?: number
  state: EquipmentRegistrationFlowState
  context: EquipmentRegistrationFlowContext
  /** A fila que o servidor refaz no commit, em ordem. */
  acoes: EquipmentRegistrationActionPayload[]
  /** Chaves de CORRELAÇÃO do pedido; os ids reais vêm do recibo (o servidor os cunha). */
  idsDoCliente: { plantId: string; locationId: string; equipmentId: string }
  chaveDeIdempotencia: string
}

/** O que a tela mostra depois de um commit que não gravou (spec §3.5). */
export type FalhaDoCommit =
  /** `espera` = o servidor pediu pausa (503 com prazo, 429); sem ela, a rede caiu. */
  | { tipo: 'tentar_de_novo'; espera?: { segundos?: number } }
  | { tipo: 'recusado'; codigo: CodigoDeRecusaDeCadastro }
  | { tipo: 'conflito'; codigo: CodigoDeConflitoDeCadastro }

export type EnvioDoCommit =
  | { fase: 'enviando' }
  /** O rascunho está indo ao servidor antes de um texto livre (§2.4): cliques parados. */
  | { fase: 'sincronizando' }
  | { fase: 'falhou'; falha: FalhaDoCommit }

/** A chave do cartão que hospedou um rascunho: o fluxo e a revision em que ele nasceu. */
export function chaveDoHospedeiro(flowId: string, base: number | undefined): string {
  return `${flowId}@${base ?? ''}`
}

/** O cadastro que o recibo encerrou sobre o cartão nascido na revision `base`. */
export interface ConcluidoLocalmente {
  base: number
  /**
   * A revisão que a pessoa confirmou. O `done` tem UM dono só (anti-redundância):
   * o cartão da mensagem que o motor gravou no epílogo do commit. O cartão que
   * hospedou o rascunho, congelado, mostra esta revisão — só leitura.
   */
  revisao: EquipmentRegistrationFlowSnapshot
  /** `salvando` = a releitura da conversa ainda não trouxe o cartão do epílogo. */
  fase: 'salvando' | 'relido'
}

interface EquipmentRegistrationFlowStore {
  /** Snapshots keyed by flowId. */
  flows: Record<string, EquipmentRegistrationFlowSnapshot>

  /**
   * Seed a snapshot from the assistant's rich-content envelope. Idempotent —
   * preserves a more recent snapshot delivered by a WS state-update that
   * already arrived (race on reconnect / replay).
   *
   * Race guard: an incoming snapshot older than the stored one (by the engine's
   * `revision`) is silently discarded. NEVER by step order — see the file header.
   */
  applySnapshot: (snapshot: EquipmentRegistrationFlowSnapshot) => void

  /**
   * Apply an engine-pushed state update over the WebSocket. Wins over the
   * stored snapshot unless it is demonstrably OLDER (`revision`), which the
   * Redis pub/sub path can deliver out of order. A legitimate rollback (the
   * user pressed back) carries a NEWER revision and is applied normally.
   */
  applyStateUpdate: (event: EquipmentRegistrationFlowStateUpdateEvent) => void

  /**
   * Local optimistic patch — used by the card when the user edits fields
   * before clicking Confirm. The patch is overwritten by the next
   * server-pushed state-update event.
   */
  patchContext: (flowId: string, patch: Partial<EquipmentRegistrationFlowContext>) => void

  /** Read a single snapshot by flowId. */
  getSnapshot: (flowId: string) => EquipmentRegistrationFlowSnapshot | undefined

  /** Drop a snapshot from the store. */
  clear: (flowId: string) => void

  /** Envelope válido do dono atual, ou `null` (⇒ caminho de hoje). */
  envelope: (EnvelopeEmUso & { dono: string }) | null
  rascunhos: Record<string, RascunhoDeCadastro>
  envios: Record<string, EnvioDoCommit>
  /** O cartão congelado que hospedou o rascunho mostra o `done` do recibo. */
  /** Chave `chaveDoHospedeiro(flowId, base)`: um registro por cartão que andou localmente. */
  concluidos: Record<string, ConcluidoLocalmente>

  definirEnvelope: (envelope: (EnvelopeEmUso & { dono: string }) | null) => void
  registrarLocalizacoes: (plantId: string, lista: readonly LocalizacaoConhecida[]) => void
  gravarRascunho: (rascunho: RascunhoDeCadastro) => void
  marcarEnvio: (flowId: string, envio: EnvioDoCommit | null) => void
  /** O snapshot do recibo vence sempre: substitui o rascunho (P13). */
  aplicarRecibo: (
    flowId: string,
    snapshot: EquipmentRegistrationFlowSnapshot | null,
    concluido?: ConcluidoLocalmente,
  ) => void
  /** A releitura depois do commit voltou (com ou sem o cartão do epílogo). */
  marcarRelido: (flowId: string) => void
  /** Troca de dono / logout: envelope e rascunhos somem juntos (P4). */
  descartarDoDono: () => void
}

/** Snapshot do servidor MAIS NOVO que a base do rascunho ⇒ o rascunho morre. */
function semRascunhoVencido(
  rascunhos: Record<string, RascunhoDeCadastro>,
  flowId: string,
  revision: number | undefined,
): Record<string, RascunhoDeCadastro> {
  const r = rascunhos[flowId]
  if (!r || revision === undefined || revision <= r.base.revision) return rascunhos
  const next = { ...rascunhos }
  delete next[flowId]
  return next
}

/**
 * Snapshot do servidor ACEITO: o rascunho vencido morre e a falha do último
 * commit também, se o snapshot é mais novo que o guardado — senão um cartão
 * recusado ficaria congelado por cima do passo novo do servidor.
 */
function aoAceitar(
  state: EquipmentRegistrationFlowStore,
  flowId: string,
  revision: number | undefined,
): Pick<EquipmentRegistrationFlowStore, 'rascunhos' | 'envios'> {
  const guardada = state.flows[flowId]?.revision
  const maisNovo = revision !== undefined && (guardada === undefined || revision > guardada)
  return {
    rascunhos: semRascunhoVencido(state.rascunhos, flowId, revision),
    envios:
      maisNovo && state.envios[flowId]?.fase === 'falhou'
        ? semChave(state.envios, flowId)
        : state.envios,
  }
}

function semChave<T>(mapa: Record<string, T>, chave: string): Record<string, T> {
  if (!(chave in mapa)) return mapa
  const next = { ...mapa }
  delete next[chave]
  return next
}

type Set = StoreApi<EquipmentRegistrationFlowStore>['setState']
type FatiaDoRascunho = Pick<
  EquipmentRegistrationFlowStore,
  | 'envelope'
  | 'rascunhos'
  | 'envios'
  | 'concluidos'
  | 'marcarRelido'
  | 'definirEnvelope'
  | 'registrarLocalizacoes'
  | 'gravarRascunho'
  | 'marcarEnvio'
  | 'aplicarRecibo'
  | 'descartarDoDono'
>

/** O envelope e o rascunho local (spec 2026-10-24 §2.2) — fatia do store. */
function estadoDoRascunho(set: Set): FatiaDoRascunho {
  return {
    envelope: null,
    rascunhos: {},
    envios: {},
    concluidos: {},

    definirEnvelope: (envelope) => set({ envelope }),

    registrarLocalizacoes: (plantId, lista) => {
      set((state) =>
        state.envelope
          ? {
              envelope: {
                ...state.envelope,
                localizacoesBuscadas: { ...state.envelope.localizacoesBuscadas, [plantId]: lista },
              },
            }
          : state,
      )
    },

    gravarRascunho: (rascunho) =>
      set((state) => ({ rascunhos: { ...state.rascunhos, [rascunho.flowId]: rascunho } })),

    marcarEnvio: (flowId, envio) =>
      set((state) => ({
        envios: envio ? { ...state.envios, [flowId]: envio } : semChave(state.envios, flowId),
      })),

    aplicarRecibo: (flowId, snapshot, concluido) => {
      set((state) => ({
        flows:
          snapshot && canReplace(state.flows[flowId], snapshot)
            ? { ...state.flows, [flowId]: snapshot }
            : state.flows,
        rascunhos: semChave(state.rascunhos, flowId),
        concluidos: concluido
          ? { ...state.concluidos, [chaveDoHospedeiro(flowId, concluido.base)]: concluido }
          : state.concluidos,
      }))
    },

    marcarRelido: (flowId) =>
      set((state) => ({
        concluidos: Object.fromEntries(
          Object.entries(state.concluidos).map(([k, c]) => [
            k,
            k.startsWith(`${flowId}@`) ? { ...c, fase: 'relido' as const } : c,
          ]),
        ),
      })),

    descartarDoDono: () => set({ envelope: null, rascunhos: {}, envios: {}, concluidos: {} }),
  }
}

export const useEquipmentRegistrationFlowStore = create<EquipmentRegistrationFlowStore>(
  (set, get) => ({
    flows: {},

    applySnapshot: (snapshot) => {
      set((state) => {
        if (!canReplace(state.flows[snapshot.flowId], snapshot)) return state
        return {
          flows: { ...state.flows, [snapshot.flowId]: snapshot },
          ...aoAceitar(state, snapshot.flowId, snapshot.revision),
        }
      })
    },

    applyStateUpdate: (event) => {
      set((state) => {
        if (!canReplace(state.flows[event.flowId], event)) return state
        const next: EquipmentRegistrationFlowSnapshot = {
          flowId: event.flowId,
          state: event.state,
          context: event.context,
          ...(typeof event.revision === 'number' ? { revision: event.revision } : {}),
        }
        return {
          flows: { ...state.flows, [event.flowId]: next },
          ...aoAceitar(state, event.flowId, event.revision),
        }
      })
    },

    patchContext: (flowId, patch) => {
      set((state) => {
        const existing = state.flows[flowId]
        if (!existing) return state
        const nextContext: EquipmentRegistrationFlowContext = { ...existing.context }
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined) continue
          ;(nextContext as Record<string, unknown>)[k] = v
        }
        return {
          flows: {
            ...state.flows,
            [flowId]: { ...existing, context: nextContext },
          },
        }
      })
    },

    getSnapshot: (flowId) => get().flows[flowId],

    clear: (flowId) => {
      set((state) => {
        const next = { ...state.flows }
        delete next[flowId]
        return { flows: next }
      })
    },

    ...estadoDoRascunho(set),
  }),
)
